import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SERVER_EVENTS } from '@superchat/shared/events';
import { CONVERSATION_STATUSES } from '@superchat/shared/constants';
import {
  Search,
  SlidersHorizontal,
  Check,
  AlertCircle,
  Loader2,
  Pencil,
  PanelRight,
  Globe,
  Copy,
  MailWarning,
  X,
  ChevronLeft,
  Pin,
  PinOff,
  Eye,
  Users,
  Ban,
} from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';
import { useAuth } from '../lib/auth.jsx';
import { usePanels } from '../lib/panels.jsx';
import { useResizableWidth, ResizeHandle } from '../lib/resizable.jsx';
import { api } from '../lib/api.js';
import { shortTime } from '../lib/format.js';
import { useUploads } from '../lib/upload.js';
import AlertModeMenu from '../components/AlertModeMenu.jsx';
import NotificationsMenu from '../components/NotificationsMenu.jsx';
import Inbox from '../components/Inbox.jsx';
import Thread from '../components/Thread.jsx';
import Composer from '../components/Composer.jsx';
import ContextRail from '../components/ContextRail.jsx';
import AttachToTicketDialog from '../components/AttachToTicketDialog.jsx';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'mine', label: 'Mine' },
  { key: 'urgent', label: 'Urgent' },
  { key: 'online', label: 'Online' },
  // The block list. Blocked threads appear here and nowhere else.
  { key: 'blocked', label: 'Blocked' },
];

/**
 * The most recent sign of life, from either signal.
 *
 * `lastMessageAt` only moves when someone writes; `session.lastSeenAt` moves as
 * the merchant clicks around their admin. A thread with no messages has a
 * `lastMessageAt` of "when the conversation was created", which is why the two
 * disagreed by four minutes in the header and the rail.
 */
function lastActivityOf(conversation) {
  const times = [conversation.lastMessageAt, conversation.session?.lastSeenAt]
    .map((value) => (value ? new Date(value).getTime() : 0))
    .filter(Boolean);
  return times.length ? new Date(Math.max(...times)) : null;
}

/**
 * The page the merchant is actually on, in full.
 *
 * `topUrl` is the top-level document — the one they would read out to you over
 * the phone. It falls back to the top origin and then to the frame's own
 * origin, because an embedded widget cannot always see its parent's URL and a
 * bare host is still better than nothing.
 */
function mainUrl(conversation) {
  const session = conversation?.session;
  return session?.topUrl || session?.topOrigin || session?.origin || '';
}

let seq = 0;
const nextClientId = () =>
  `d_${Date.now().toString(36)}_${(seq += 1)}_${Math.random().toString(36).slice(2, 8)}`;

export function ChatView() {
  const {
    activeAppId,
    allApps,
    appIndex,
    apps,
    loadingApps,
    selectApp,
    conversations,
    loadingConversations,
    membersByApp,
    patchConversation,
    setOpenConversation,
    onSocketEvent,
    socket,
    canOn,
    isVisitorOnline,
    isPinned,
    toggleConversationPin,
    conversationViewers,
  } = useWorkspace();

  const { user } = useAuth();
  const uploads = useUploads();

  const { compact, mobile, rail: railOpen, toggle: togglePanel } = usePanels();

  /**
   * The inbox is the one column whose right width is a matter of taste: an
   * agency reading store names needs more of it than someone working a queue of
   * two. Remembered per browser, and off on a phone, where the list is already
   * the whole screen and there is no border beside it to drag.
   *
   * The default follows the layout rather than overriding it — a wider inbox at
   * full width, a narrower one once a side panel is competing for the room —
   * which is why the CSS keeps its own defaults and this only sets a variable.
   */
  const inbox = useResizableWidth({
    key: 'superchat:inboxWidth',
    initial: compact ? 264 : 328,
    enabled: !mobile,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(null);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [visitorTyping, setVisitorTyping] = useState(false);
  /** The colleague composing in this thread right now, by name, or null. */
  const [agentTyping, setAgentTyping] = useState(null);
  const [sending, setSending] = useState(false);
  // Set when a reply went out but the email the agent asked for did not.
  const [emailWarning, setEmailWarning] = useState(null);
  /** A refused edit or delete — the server's reason, verbatim. */
  const [actionError, setActionError] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  // The attachment an agent is filing against a ticket, if any.
  const [filing, setFiling] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const active = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId]
  );

  /**
   * Every request about a thread goes to the app that thread belongs to, not to
   * whatever is selected — which is what lets one inbox hold several tenants.
   * With a single app in focus the two are the same value.
   */
  const threadAppId = active?.appId ?? activeAppId;
  const threadApp = threadAppId ? appIndex[threadAppId] : null;
  const canSend = threadAppId ? canOn(threadAppId, 'chat', 'send') : false;
  const threadMembers = membersByApp[threadAppId] ?? [];

  /**
   * Who holds this conversation.
   *
   * The server orders viewers by when they joined, so the first entry is the
   * agent who opened it first — and that agent replies without being asked
   * anything. Everyone who arrives after them is walking into a thread somebody
   * else is already handling, which is the case worth a confirmation.
   *
   * An empty list means nobody has subscribed yet, including us, which is the
   * ordinary single-agent case: `holder` is null and nothing is in the way.
   */
  const viewers = useMemo(
    () => (activeId ? (conversationViewers[activeId] ?? []) : []),
    [conversationViewers, activeId]
  );
  const otherViewers = useMemo(
    () => viewers.filter((v) => v.userId !== user?.id),
    [viewers, user?.id]
  );
  const holder = viewers[0] ?? null;
  const iHoldIt = !holder || holder.userId === user?.id;

  /**
   * `chat: send` grants replying, not rewriting. The author may fix their own
   * words and the app's owner may pull anyone's — the same rule the server
   * enforces, mirrored here so the buttons only appear where they would work.
   */
  /** Who pinned this thread, when somebody did — a shared pin says whose. */
  const pinnedByName = useMemo(() => {
    if (!active?.pinnedBy) return null;
    const member = threadMembers.find((m) => m.userId === active.pinnedBy);
    if (!member) return null;
    return member.userId === user?.id ? 'You' : member.name || member.email;
  }, [active?.pinnedBy, threadMembers, user?.id]);

  const ownsThreadApp = Boolean(threadApp?.isOwner);
  const canModifyMessage = useCallback(
    (message) =>
      canSend &&
      message.senderType === 'agent' &&
      !message.deleted &&
      (ownsThreadApp || String(message.senderId) === String(user?.id)),
    [canSend, ownsThreadApp, user?.id]
  );

  /* Pick the first row once the list lands, and whenever the scope changes. */
  useEffect(() => {
    setActiveId(null);
    setMessages([]);
  }, [activeAppId, allApps]);

  /**
   * A `?c=` deep link wins over the default selection — that is how the online
   * users panel, a push notification click and an email link all land on a
   * thread.
   *
   * An emailed link also carries `?app=`, because it may be opened days later
   * with a different tenant in focus, and a conversation id means nothing until
   * the right one is selected. Switching apps re-runs this effect, which is why
   * `c` is only consumed once the app has caught up.
   */
  useEffect(() => {
    const requested = searchParams.get('c');
    if (!requested) return;

    // In the all-apps scope the thread is already in this list whichever tenant
    // it belongs to, so there is nothing to switch to.
    const requestedApp = allApps ? null : searchParams.get('app');
    if (requestedApp && requestedApp !== activeAppId) {
      if (loadingApps) return; // the list decides whether it is reachable
      if (apps.some((a) => a.id === requestedApp)) {
        selectApp(requestedApp);
        return;
      }
      // Not an app this agent can reach. Ignore the hint rather than stranding
      // them on a blank view — the conversation lookup will fail visibly.
    }

    setActiveId(requested);
    // Consume it, so a later manual selection is not undone by a stale URL.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, activeAppId, allApps, apps, loadingApps, selectApp]);

  /**
   * Land on the newest thread — but not on a phone, where the inbox and the
   * thread take turns holding the whole screen. Auto-selecting there would
   * open a conversation nobody asked for and hide the list behind it, so the
   * agent's first action would always be to press Back.
   */
  useEffect(() => {
    if (mobile || activeId) return;
    // Never land on a blocked thread — the point of blocking is not seeing it.
    const first = conversations.find((c) => !c.blocked);
    if (first) setActiveId(first.id);
  }, [conversations, activeId, mobile]);

  /* ---------------------------------------------------------------- thread */

  const loadThread = useCallback(
    async (appId, conversationId) => {
      if (!appId || !conversationId) return;
      setLoadingThread(true);
      try {
        const { data, meta } = await api.conversations.messages(appId, conversationId, {
          limit: 40,
        });
        // The API paginates backwards (newest first); display is chronological.
        setMessages([...data].reverse());
        setCursor(meta?.nextCursor ?? null);
        setHasMore(Boolean(meta?.hasMore));
      } catch {
        setMessages([]);
      } finally {
        setLoadingThread(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!activeId || !threadAppId) return undefined;

    setVisitorTyping(false);
    setAgentTyping(null);
    loadThread(threadAppId, activeId);
    setOpenConversation(activeId);
    socket.current?.subscribe(activeId);

    return () => {
      socket.current?.unsubscribe(activeId);
      setOpenConversation(null);
    };
  }, [activeId, threadAppId, loadThread, setOpenConversation, socket]);

  /* Clear the badge once the thread is actually on screen. */
  useEffect(() => {
    if (!threadAppId || !active || active.unread.agent === 0) return;
    api.conversations
      .markRead(threadAppId, active.id)
      .then((result) => patchConversation(result.conversation))
      .catch(() => {});
  }, [threadAppId, active, patchConversation]);

  /* ------------------------------------------------------------- realtime */

  useEffect(
    () =>
      onSocketEvent((event, payload) => {
        if (event === SERVER_EVENTS.MESSAGE_NEW) {
          if (payload.conversationId !== activeIdRef.current) return;
          setMessages((current) => {
            const without = current.filter(
              (m) =>
                m.id !== payload.id &&
                !(payload.clientId && m.clientId && m.clientId === payload.clientId)
            );
            return [...without, payload].sort(
              (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
            );
          });
          if (payload.senderType === 'visitor') setVisitorTyping(false);
          if (payload.senderType === 'agent') setAgentTyping(null);
          return;
        }

        /**
         * An agent rewrote or withdrew a message. Patched in place rather than
         * appended — the row is already on screen, and re-sorting the thread
         * for an edit would move a two-day-old correction to the bottom.
         */
        if (event === SERVER_EVENTS.MESSAGE_UPDATED) {
          if (payload.conversationId !== activeIdRef.current) return;
          setMessages((current) => current.map((m) => (m.id === payload.id ? payload : m)));
          return;
        }

        if (event === SERVER_EVENTS.MESSAGE_READ) {
          if (payload.conversationId !== activeIdRef.current) return;
          if (payload.readerSide === 'visitor') {
            setMessages((current) =>
              current.map((m) =>
                m.senderType === 'agent' && !m.readAt ? { ...m, readAt: payload.readAt } : m
              )
            );
          }
          return;
        }

        if (event === SERVER_EVENTS.TYPING) {
          if (payload.conversationId !== activeIdRef.current) return;
          if (payload.senderType === 'visitor') setVisitorTyping(Boolean(payload.isTyping));
          // The relay already excludes the sender's own socket, so anything
          // arriving here from an agent is genuinely somebody else.
          if (payload.senderType === 'agent') {
            setAgentTyping(payload.isTyping ? payload.senderName || 'Another agent' : null);
          }
        }
      }),
    [onSocketEvent]
  );

  /* --------------------------------------------------------------- actions */

  const send = useCallback(
    async ({ body, isEmergency, attachments = [], notifyByEmail = false }) => {
      const text = body.trim();
      // An attachment on its own is a message; the API agrees.
      if ((!text && attachments.length === 0) || !active) return;

      const clientId = nextClientId();
      const optimistic = {
        id: clientId,
        clientId,
        conversationId: active.id,
        senderType: 'agent',
        senderName: '',
        body: text,
        attachments,
        isEmergency,
        readAt: null,
        createdAt: new Date().toISOString(),
        pending: true,
      };

      setMessages((current) => [...current, optimistic]);
      setSending(true);
      socket.current?.setTyping(active.id, false);

      try {
        // clientId makes the send idempotent — a retry returns the original
        // message with 200 rather than creating a duplicate.
        const saved = await api.conversations.send(active.appId, active.id, {
          body: text,
          isEmergency,
          attachments,
          clientId,
          notifyByEmail,
        });
        setMessages((current) =>
          current.map((m) => (m.clientId === clientId ? saved : m))
        );

        /**
         * The message went; the email did not.
         *
         * Worth saying out loud rather than swallowing: the agent chose to
         * reach someone who is not on the page, and silently doing only half
         * of that leaves them believing a customer has been told something
         * they have not.
         */
        if (notifyByEmail && saved?.email && !saved.email.sent) {
          setEmailWarning(
            saved.email.error === 'not_configured'
              ? 'Reply sent, but this app has no email sender connected.'
              : saved.email.error === 'no_recipient'
                ? 'Reply sent, but there is no email address on record for this visitor.'
                : 'Reply sent, but the email could not be delivered.'
          );
        }
      } catch {
        setMessages((current) =>
          current.map((m) =>
            m.clientId === clientId ? { ...m, pending: false, failed: true } : m
          )
        );
      } finally {
        setSending(false);
      }
    },
    [active, socket]
  );

  /**
   * The single-support rule, as something the composer can ask about.
   *
   * The agent who opened the thread first replies without being asked anything
   * — that is the ordinary case and it has to stay frictionless. Anyone who
   * arrived after them is walking into a thread somebody else is already
   * handling, and gets one confirmation before their reply goes out. Null the
   * rest of the time, which is what turns the prompt off entirely.
   *
   * A nudge, not a lock: two people genuinely working one incident together
   * must not be barred from both answering, and the confirmation is one click.
   */
  const contention = useMemo(() => {
    if (iHoldIt || otherViewers.length === 0) return null;
    const typist = otherViewers.find((v) => v.typing);
    return {
      holderName: holder?.name || 'Another agent',
      typingName: typist ? typist.name || 'Another agent' : null,
    };
  }, [iHoldIt, otherViewers, holder]);

  /* -------------------------------------------------- editing what was sent */

  /**
   * Both of these let the socket do the reconciling.
   *
   * The server broadcasts MESSAGE_UPDATED to the conversation room, which this
   * view is in, so the response is applied here only as insurance against the
   * event being missed — patching by id either way, so arriving twice is a
   * no-op rather than a duplicate.
   */
  const editMessage = useCallback(
    async (message, body) => {
      const saved = await api.conversations.editMessage(
        active.appId,
        active.id,
        message.id,
        body
      );
      setMessages((current) => current.map((m) => (m.id === saved.id ? saved : m)));
    },
    [active]
  );

  const deleteMessage = useCallback(
    async (message) => {
      if (
        !window.confirm(
          'Delete this message? It disappears from the visitor’s chat too, leaving a "message deleted" line where it was.'
        )
      ) {
        return;
      }
      try {
        const saved = await api.conversations.deleteMessage(active.appId, active.id, message.id);
        setMessages((current) => current.map((m) => (m.id === saved.id ? saved : m)));
      } catch (err) {
        setActionError(err.message);
      }
    },
    [active]
  );

  const loadEarlier = useCallback(async () => {
    if (!hasMore || !cursor || !active) return;
    const { data, meta } = await api.conversations.messages(active.appId, active.id, {
      cursor,
      limit: 40,
    });
    setMessages((current) => [...[...data].reverse(), ...current]);
    setCursor(meta?.nextCursor ?? null);
    setHasMore(Boolean(meta?.hasMore));
  }, [active, cursor, hasMore]);

  const updateConversation = useCallback(
    async (patch) => {
      if (!active) return;
      const updated = await api.conversations.update(active.appId, active.id, patch);
      patchConversation(updated);
    },
    [active, patchConversation]
  );

  const setTyping = useCallback(
    (isTyping) => active && socket.current?.setTyping(active.id, isTyping),
    [active, socket]
  );

  /** Renaming writes to `subject`; clearing it falls back to the visitor name. */
  const commitRename = useCallback(() => {
    setRenaming(false);
    const next = titleDraft.trim();
    if (!active || next === (active.subject ?? '')) return;
    updateConversation({ subject: next });
  }, [active, titleDraft, updateConversation]);

  // Leaving a thread mid-rename must not carry the draft to the next one.
  useEffect(() => {
    setRenaming(false);
  }, [activeId]);

  /* ------------------------------------------------------------- filtering */

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conversations.filter((c) => {
      // Blocked threads live on their own tab and are hidden from every other.
      if (tab === 'blocked' ? !c.blocked : c.blocked) return false;
      if (tab === 'online' && !isVisitorOnline(c.visitor.visitorId)) return false;
      if (tab === 'unassigned' && c.assigneeId) return false;
      if (tab === 'urgent' && !c.hasEmergency) return false;
      // "Mine" means assigned to me — it used to mean "assigned to anyone",
      // which showed a colleague's queue under your own name.
      if (tab === 'mine' && c.assigneeId !== user?.id) return false;
      if (!needle) return true;
      return [c.visitor.name, c.visitor.email, c.subject, c.lastMessagePreview]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle));
    });
  }, [conversations, tab, query, user?.id, isVisitorOnline]);

  const counts = useMemo(() => {
    const visible = conversations.filter((c) => !c.blocked);
    return {
      all: visible.length,
      unassigned: visible.filter((c) => !c.assigneeId).length,
      mine: visible.filter((c) => c.assigneeId === user?.id).length,
      urgent: visible.filter((c) => c.hasEmergency).length,
      online: visible.filter((c) => isVisitorOnline(c.visitor.visitorId)).length,
      blocked: conversations.length - visible.length,
    };
  }, [conversations, user?.id, isVisitorOnline]);

  const openCount = conversations.filter((c) => c.status === 'open' && !c.blocked).length;

  return (
    /* On a phone the two panes are one screen each and this class says which
       of them is showing. Above the breakpoint it means nothing — they are
       side by side and both are always up. */
    <div className={`chat${active ? ' chat--reading' : ''}`}>
      <section
        className="inbox"
        // A custom property rather than `width`, so the breakpoints keep their
        // own defaults and the mobile layout — which sets `width: 100%` and
        // never reads this — is not overridden by an inline style.
        style={inbox.width ? { '--inbox-w': `${inbox.width}px` } : undefined}
      >
        <header className="inbox__head">
          <h1 className="inbox__title">Inbox</h1>
          <span className="inbox__count">{openCount} open</span>
          <div className="inbox__tools">
            {/* The bell is what happened; the speaker is how it sounds. */}
            <NotificationsMenu />
            <AlertModeMenu />
            <button type="button" className="icon-button" title="Filters">
              <SlidersHorizontal size={14} />
            </button>
          </div>
        </header>

        <div className="inbox__search">
          <Search size={13} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search store, message, order…"
            aria-label="Search conversations"
          />
        </div>

        <div className="inbox__tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab${tab === t.key ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className="tab__count">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        <Inbox
          // A new tab, search or scope starts back on the first page.
          key={`${allApps ? '*' : activeAppId}|${tab}|${query.trim()}`}
          conversations={filtered}
          loading={loadingConversations}
          activeId={activeId}
          onSelect={setActiveId}
        />
      </section>

      {/* The inbox's right border, made draggable. Rendered outside the panel
          so it is not clipped by its `overflow: hidden` and does not cover the
          list's own scrollbar. */}
      {!mobile && (
        <ResizeHandle
          label="Resize the inbox — drag, or use the arrow keys"
          dragging={inbox.dragging}
          {...inbox.handleProps}
        />
      )}

      <section className="thread-pane">
        {!active && (
          <div className="empty">
            {loadingConversations ? 'Loading conversations…' : 'No conversation selected'}
          </div>
        )}

        {active && (
          <>
            <header className="thread-head">
              {/* The way back to the list. Only a phone needs it — everywhere
                  else the inbox never left the screen — so it is CSS-hidden
                  above the breakpoint rather than conditionally rendered. */}
              <button
                type="button"
                className="icon-button thread-head__back"
                onClick={() => setActiveId(null)}
                aria-label="Back to the inbox"
                title="Back to the inbox"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="thread-head__mark">
                {(active.visitor.name || 'V').charAt(0).toUpperCase()}
              </span>
              <div className="thread-head__text">
                <div className="thread-head__row">
                  {renaming ? (
                    <input
                      className="thread-head__rename"
                      autoFocus
                      value={titleDraft}
                      placeholder={active.visitor.name || active.visitor.visitorId}
                      maxLength={300}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        // Escape abandons the edit rather than saving a partial.
                        if (e.key === 'Escape') setRenaming(false);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="thread-head__name"
                      onClick={() => {
                        setTitleDraft(active.subject ?? '');
                        setRenaming(true);
                      }}
                      title="Rename this chat"
                    >
                      {active.subject || active.visitor.name || active.visitor.visitorId}
                      <Pencil size={11} className="thread-head__pencil" />
                    </button>
                  )}
                  {active.subject && (
                    <span className="thread-head__who">
                      {active.visitor.name || active.visitor.visitorId}
                    </span>
                  )}
                  {/* Which tenant this thread is on. Only worth the pixels
                      when the inbox is holding more than one. */}
                  {allApps && threadApp && (
                    <span
                      className="thread-head__app"
                      style={{ color: threadApp.color, borderColor: threadApp.color }}
                    >
                      {threadApp.name}
                    </span>
                  )}
                  {active.hasEmergency && (
                    <span className="flag flag--danger">USER ALERT</span>
                  )}
                </div>
                <div className="thread-head__meta">
                  {active.visitor.email || '—'}
                  <span className="dot-sep">·</span>
                  {/* This used to read "Last activity" while showing the last
                      *message* time — on a thread with no messages that is the
                      moment the conversation was created, which contradicted
                      the rail's "Last seen" a few pixels away. Presence is the
                      honest answer when they are connected; otherwise take
                      whichever of the two signals is genuinely more recent. */}
                  {visitorTyping
                    ? 'Typing…'
                    : isVisitorOnline(active.visitor.visitorId)
                      ? 'Active now'
                      : `Last seen ${shortTime(lastActivityOf(active))}`}
                </div>
              </div>

              <div className="thread-head__actions">
                <button
                  type="button"
                  className={`icon-button${isPinned(active) ? ' is-active' : ''}`}
                  onClick={() => toggleConversationPin(active.id)}
                  disabled={!canSend}
                  aria-pressed={isPinned(active)}
                  title={
                    isPinned(active)
                      ? `Pinned to the top of the inbox for everyone on this app${
                          pinnedByName ? ` — ${pinnedByName} put it there` : ''
                        }`
                      : 'Pin to the top of the inbox for everyone on this app'
                  }
                >
                  {isPinned(active) ? <PinOff size={14} /> : <Pin size={14} />}
                </button>
                <button
                  type="button"
                  className={`icon-button${active.blocked ? ' is-danger' : ''}`}
                  onClick={() => {
                    if (
                      !active.blocked &&
                      !window.confirm(
                        'Block this chat? It leaves the inbox and nothing about it will notify anyone — alerts included — until it is unblocked from the Blocked tab.'
                      )
                    ) {
                      return;
                    }
                    updateConversation({ blocked: !active.blocked }).catch((err) =>
                      setActionError(err.message)
                    );
                  }}
                  disabled={!canSend}
                  aria-pressed={active.blocked}
                  title={active.blocked ? 'Blocked — click to unblock' : 'Block this chat'}
                >
                  <Ban size={14} />
                </button>
                {/* Who owns this thread. In the header rather than the context
                    rail because it decides who answers, which is a decision an
                    agent takes before reading anything else — and the rail is
                    collapsed half the time. */}
                <select
                  className={`select${active.assigneeId ? ' is-assigned' : ''}`}
                  value={active.assigneeId ?? ''}
                  disabled={!canSend}
                  onChange={(e) => updateConversation({ assigneeId: e.target.value || null })}
                  aria-label="Assignee"
                  title={
                    active.assigneeId
                      ? 'Reassign this conversation'
                      : 'Assign this conversation to someone'
                  }
                >
                  <option value="">Unassigned</option>
                  {/* The roster of the thread's own app — a colleague on
                      another tenant cannot be handed this conversation. */}
                  {threadMembers.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.name || member.email}
                      {member.userId === user?.id ? ' (you)' : ''}
                    </option>
                  ))}
                  {/* Assigned to someone since removed from the app: without
                      this the select falls back to the first option and reads
                      as unassigned, which it is not. */}
                  {active.assigneeId &&
                    !threadMembers.some((m) => m.userId === active.assigneeId) && (
                      <option value={active.assigneeId}>Former member</option>
                    )}
                </select>
                <select
                  className="select"
                  value={active.status}
                  disabled={!canSend}
                  onChange={(e) => updateConversation({ status: e.target.value })}
                  aria-label="Conversation status"
                >
                  {CONVERSATION_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="icon-button"
                  title="Mark resolved"
                  disabled={!canSend}
                  onClick={() => updateConversation({ status: 'resolved' })}
                >
                  <Check size={14} />
                </button>
                <button
                  type="button"
                  className={`icon-button${railOpen ? ' is-active' : ''}`}
                  onClick={() => togglePanel('rail')}
                  aria-expanded={railOpen}
                  aria-label={railOpen ? 'Collapse context panel' : 'Expand context panel'}
                  title={
                    railOpen
                      ? 'Collapse context panel'
                      : compact
                        ? 'Expand context panel — this closes the menu'
                        : 'Expand context panel'
                  }
                >
                  <PanelRight size={14} />
                </button>
              </div>
            </header>

            {/* The page the merchant is on, in full and in one piece.
                A truncated host was enough to know *which* store; it was not
                enough to know which screen of it, which is what an agent
                actually needs before they can answer anything. */}
            {mainUrl(active) && (
              <div className="thread-origin">
                <Globe size={12} />
                <a
                  className="thread-origin__url mono"
                  href={mainUrl(active)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={mainUrl(active)}
                >
                  {mainUrl(active)}
                </a>
                {active.session?.embedded && (
                  <span
                    className="thread-head__embed"
                    title="The widget runs in a nested iframe, so this is the parent page it reported"
                  >
                    IFRAME
                  </span>
                )}
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => {
                    navigator.clipboard.writeText(mainUrl(active)).then(
                      () => setCopiedUrl(true),
                      () => {}
                    );
                    setTimeout(() => setCopiedUrl(false), 2000);
                  }}
                  title="Copy this URL"
                  aria-label="Copy the page URL"
                >
                  {copiedUrl ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}

            {/* Somebody else is in here.
                Above the thread rather than beside the composer, because the
                useful moment to learn it is while reading the history — before
                a second answer to the same question has been typed. The wording
                distinguishes the two states: reading is a heads-up, typing is
                a collision about to happen. */}
            {otherViewers.length > 0 && (
              <div
                className={`viewers-banner${
                  otherViewers.some((v) => v.typing) ? ' is-typing' : ''
                }`}
                role="status"
              >
                {otherViewers.length > 1 ? <Users size={12} /> : <Eye size={12} />}
                <span className="viewers-banner__text">
                  {otherViewers.length === 1
                    ? `${otherViewers[0].name || 'Another agent'} ${
                        otherViewers[0].typing
                          ? 'is typing a reply here'
                          : 'has this conversation open'
                      }`
                    : `${otherViewers.map((v) => v.name || 'An agent').join(', ')} have this conversation open`}
                </span>
                {/* Who the thread belongs to, when it is not you. Naming it is
                    what makes the confirmation below feel like a rule rather
                    than an interruption. */}
                {!iHoldIt && holder && (
                  <span className="viewers-banner__holder">
                    {holder.name || 'They'} opened it first
                  </span>
                )}
              </div>
            )}

            {actionError && (
              <div className="composer__warning" role="status">
                <AlertCircle size={13} />
                <span>{actionError}</span>
                <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss">
                  <X size={12} />
                </button>
              </div>
            )}

            {active.blocked && (
              <div className="composer__warning" role="status">
                <Ban size={13} />
                <span>
                  This chat is blocked. It is hidden from the inbox and will not notify anyone.
                </span>
                <button
                  type="button"
                  className="ghost-button"
                  disabled={!canSend}
                  onClick={() =>
                    updateConversation({ blocked: false }).catch((err) =>
                      setActionError(err.message)
                    )
                  }
                >
                  Unblock
                </button>
              </div>
            )}

            {active.hasEmergency && (
              <div className="emergency-banner">
                <span className="emergency-banner__dot" />
                <span className="emergency-banner__label">
                  <AlertCircle size={12} /> USER ALERT SENT
                </span>
                <span className="emergency-banner__body">
                  The chat was forced open on the merchant's screen. To them it looked like an
                  ordinary reply.
                </span>
                {/* This used to only set the status to resolved. On a thread that
                    was already resolved that changed nothing, and it never cleared
                    the alert flag itself — so the banner stayed put. */}
                <button
                  type="button"
                  className="ghost-button"
                  disabled={!canSend}
                  onClick={() =>
                    updateConversation({ hasEmergency: false }).catch((err) =>
                      setActionError(err.message)
                    )
                  }
                >
                  Stand down alert
                </button>
              </div>
            )}

            {loadingThread ? (
              <div className="empty">
                <Loader2 size={16} className="spin" /> Loading thread…
              </div>
            ) : (
              <Thread
                messages={messages}
                visitorTyping={visitorTyping}
                agentTyping={agentTyping}
                hasMore={hasMore}
                onLoadEarlier={loadEarlier}
                appId={threadAppId}
                onAttachToTicket={canSend ? setFiling : null}
                canModifyMessage={canModifyMessage}
                onEditMessage={editMessage}
                onDeleteMessage={deleteMessage}
              />
            )}

            {canSend ? (
              <>
              {emailWarning && (
                <div className="composer__warning" role="status">
                  <MailWarning size={13} />
                  <span>{emailWarning}</span>
                  <button type="button" onClick={() => setEmailWarning(null)} aria-label="Dismiss">
                    <X size={12} />
                  </button>
                </div>
              )}
              <Composer
                onSend={send}
                onTyping={setTyping}
                sending={sending}
                visitorName={active.visitor.name || 'the merchant'}
                appId={threadAppId}
                conversationId={active.id}
                uploads={uploads}
                visitorOffline={!isVisitorOnline(active.visitor.visitorId)}
                visitorEmail={active.visitor.email}
                canEmailVisitor={Boolean(threadApp?.capabilities?.visitorEmail)}
                contention={contention}
              />
              </>
            ) : (
              <div className="composer composer--readonly">
                Your access to this app is read-only. Ask the owner for reply access in Team.
              </div>
            )}
          </>
        )}
      </section>

      <ContextRail conversation={active} onUpdate={updateConversation} />

      {filing && active && (
        <AttachToTicketDialog
          appId={threadAppId}
          conversationId={active.id}
          file={filing}
          onClose={() => setFiling(null)}
        />
      )}
    </div>
  );
}

export default ChatView;
